<script lang='ts'>
import type { IDatePickerType } from 'element-plus/es/components/date-picker/src/date-picker.type'
import type { PropType } from 'vue'
import { useFormItem } from 'element-plus'
import moment from 'moment'
import { computed, defineComponent, ref, toRefs, watch } from 'vue'

type DateType = string | string[]

export default defineComponent({
  name: 'MsDatePick',
  props: {
    type: {
      type: String as PropType<IDatePickerType>,
      required: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    time: {
      type: [Number, String],
      default: 0,
    },
    start: {
      type: [Number, String],
      default: 0,
    },
    end: {
      type: [Number, String],
      default: 0,
    },
    showShortcuts: {
      type: Boolean,
      default: false,
    },
    defaultTime: {
      type: Object as PropType<DateType>,
    },
    outputFormat: {
      type: String,
    },
  },
  emits: ['update:time', 'update:start', 'update:end', 'change'],
  setup(props, ctx) {
    const {
      type,
      range,
      time,
      start,
      end,
      outputFormat,
      showShortcuts,
    } = toRefs(props)

    const { formItem } = useFormItem()

    const doInputFormat = (val: number | string) => {
      return !val ? '' : moment(val).format()
    }

    const doOutputFormat = (val: string | null) => {
      if (outputFormat.value) {
        return !val ? '' : moment(val).format(outputFormat.value)
      }
      return !val ? 0 : Date.parse(val)
    }

    const date = ref<DateType>(range.value ? [] : doInputFormat(time.value))

    const handleChange = (val: DateType | null) => {
      if (range.value) {
        let startVal = val && val[0]
        let endVal = val && val[1]
        if (type.value === 'daterange' && startVal && endVal) {
          startVal = moment(startVal).startOf('day').format()
          endVal = moment(endVal).endOf('day').format()
        }
        ctx.emit('update:start', doOutputFormat(startVal))
        ctx.emit('update:end', doOutputFormat(endVal))
      } else {
        ctx.emit('update:time', doOutputFormat(val as string))
      }
      ctx.emit('change', val)
      formItem?.validate?.('change').catch(err => console.warn(err))
    }

    watch([time, start, end], ([timeVal, startVal, endVal]) => {
      if (range.value) {
        date.value = [doInputFormat(startVal), doInputFormat(endVal)]
      } else {
        date.value = doInputFormat(timeVal)
      }
    })

    const singleShortcutsData = [
      {
        text: '今天',
        value: (() => {
          return moment().format()
        })(),
      },
      {
        text: '昨天',
        value: (() => {
          return moment().subtract(1, 'day').format()
        })(),
      },
      {
        text: '一周前',
        value: (() => {
          return moment().subtract(1, 'weeks').format()
        })(),
      },
    ]

    const rangeShortcutsData = [
      {
        text: '今天',
        value: (() => {
          const start = moment().startOf('day').format()
          const end = moment().format()
          return [start, end]
        })(),
      },
      {
        text: '昨天',
        value: (() => {
          const start = moment().subtract(1, 'day').startOf('day').format()
          const end = moment().subtract(1, 'day').endOf('day').format()
          return [start, end]
        })(),
      },
      {
        text: '最近一周',
        value: (() => {
          const start = moment().subtract(1, 'weeks').format()
          const end = moment().format()
          return [start, end]
        })(),
      },
      {
        text: '最近一个月',
        value: (() => {
          const start = moment().subtract(30, 'days').format()
          const end = moment().format()
          return [start, end]
        })(),
      },
      {
        text: '最近三个月',
        value: (() => {
          const start = moment().subtract(90, 'days').format()
          const end = moment().format()
          return [start, end]
        })(),
      },
    ]

    const shortcutsData = computed(() => {
      const data = range.value ? rangeShortcutsData : singleShortcutsData
      return showShortcuts.value ? data : []
    })

    return {
      date,
      shortcutsData,
      handleChange,
    }
  },
})
</script>

<template>
  <el-date-picker
    v-model="date" v-bind="$attrs" :type="type" unlink-panels :shortcuts="shortcutsData"
    :editable="false" @change="handleChange"
  />
</template>

<style scoped>
</style>
